import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
} from "@floating-ui/react";
import { useRef, useState } from "react";

import { useLocationPredictions } from "@hooks/useLocationPredictions";
import { useScrollLock } from "@hooks/useScrollLock";

import { Input } from "./Input";

const DROPDOWN_GAP_PX = 4;

interface LocationAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  /** Store just the city, dropping the region/country tail. */
  cityOnly?: boolean;
}

/**
 * City field for LOCATION attributes: free text with filtered city suggestions, as in the CRM.
 * Typing is held locally and only committed on selection or blur, so a half-typed city never saves.
 */
export function LocationAutocomplete({
  value,
  onChange,
  placeholder,
  disabled,
  isInvalid,
  errorMessage,
  onFocus,
  onBlur,
  cityOnly = false,
}: LocationAutocompleteProps) {
  // null means "not editing" — the committed value is shown instead.
  const [draft, setDraft] = useState<string | null>(null);
  // Cached predictions outlive a dismissal, so track it rather than clearing them.
  const [isDismissed, setIsDismissed] = useState(false);
  const isSelectingRef = useRef(false);
  const { predictions } = useLocationPredictions(draft ?? "");

  const containerRef = useRef<HTMLDivElement>(null);

  const isOpen = draft !== null && !isDismissed && predictions.length > 0;

  useScrollLock({ enabled: isOpen, containerRef });

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      if (!open) setIsDismissed(true);
    },
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(DROPDOWN_GAP_PX),
      flip(),
      size({
        apply({ rects, elements }) {
          elements.floating.style.width = `${rects.reference.width}px`;
        },
      }),
    ],
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([useDismiss(context)]);

  const commit = (next: string) => {
    setDraft(null);
    setIsDismissed(false);

    if (next !== value) onChange(next);
  };

  const referenceRef = useMergeRefs([refs.setReference, containerRef]);

  return (
    <div ref={referenceRef} className="w-full" {...getReferenceProps()}>
      <Input
        isFullWidth
        maxLength={255}
        value={draft ?? value}
        placeholder={placeholder}
        disabled={disabled}
        isInvalid={isInvalid}
        errorMessage={errorMessage}
        onChange={(e) => {
          setDraft(e.target.value);
          setIsDismissed(false);
        }}
        onFocus={onFocus}
        onBlur={() => {
          // Picking a suggestion blurs the input first; let the click commit instead.
          if (!isSelectingRef.current) {
            commit(draft ?? value);
            onBlur?.();
          }

          isSelectingRef.current = false;
        }}
      />

      {isOpen && (
        <FloatingPortal>
          <ul
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-[9999] max-h-60 overflow-y-auto rounded-lg border border-stroke-primary bg-background-primary py-1 shadow-lg dark:border-stroke-primaryDark dark:bg-background-primaryDark"
          >
            {predictions.map(({ place_id, structured_formatting }) => {
              const { main_text: mainText, secondary_text: secondaryText } = structured_formatting;

              return (
                <li key={place_id}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-1 px-3 py-2 text-left text-sm hover:bg-background-secondary dark:hover:bg-background-secondaryDark"
                    onMouseDown={() => {
                      isSelectingRef.current = true;
                    }}
                    onClick={() => {
                      commit(
                        cityOnly || !secondaryText ? mainText : `${mainText}, ${secondaryText}`
                      );
                      onBlur?.();
                    }}
                  >
                    <span className="font-medium text-content-primary dark:text-white">
                      {mainText}
                      {secondaryText ? "," : ""}
                    </span>
                    <span className="min-w-0 truncate text-content-secondary dark:text-invertedPrimary80">
                      {secondaryText}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </FloatingPortal>
      )}
    </div>
  );
}
